import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Text,
  Stack,
  Group,
  Badge,
  Paper,
  ActionIcon,
  Divider,
  Collapse,
  Box,
  Loader,
  Flex,
  ThemeIcon,
  UnstyledButton,
  Indicator,
  Center,
} from '@mantine/core';
import {
  IconSearch,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconClipboard,
  IconDna,
  IconCheck,
  IconCirclePlus,
  IconCircleMinus,
  IconTrash,
  IconSparkles,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { HpoItem } from '@/types/hpo.types';
import { Condition, Patient, Encounter } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { usePatientHpo } from '@/hooks/usePatientHpo';
import { HL7 } from '@/consts/systems.consts';
import { fetchDiseaseHPO, getOmimIdFromCondition } from '@/utils/hpo-omim';
import { useTranslation } from 'react-i18next';

interface HpoSelectionProps {
  patient: Patient;
  encounter?: Encounter;
  selectedHpo: HpoItem[];
  setSelectedHpo: React.Dispatch<React.SetStateAction<HpoItem[]>>;
  readonly?: boolean;
}

// Enhanced HPO Badge Component
interface EnhancedHpoBadgeProps {
  item: HpoItem;
  selected: boolean;
  onToggle: (item: HpoItem) => void;
  variant?: 'past' | 'genetic' | 'search' | 'selected';
  showAnimation?: boolean;
  readonly?: boolean;
}

function EnhancedHpoBadge({
  item,
  selected,
  onToggle,
  variant = 'search',
  showAnimation = false,
  readonly = false,
}: EnhancedHpoBadgeProps) {
  const getVariantProps = () => {
    switch (variant) {
      case 'past':
        return {
          color: 'blue',
          variant: selected ? 'filled' : 'light',
          leftSection: <IconClipboard size={12} />,
        };
      case 'genetic':
        return {
          color: 'orange',
          variant: selected ? 'filled' : 'light',
          leftSection: <IconDna size={12} />,
        };
      case 'search':
        return {
          color: 'green',
          variant: selected ? 'filled' : 'light',
          leftSection: <IconSparkles size={12} />,
        };
      case 'selected':
        return {
          color: 'teal',
          variant: 'filled' as const,
          leftSection: <IconCheck size={12} />,
        };
      default:
        return {
          color: 'gray',
          variant: selected ? 'filled' : 'light',
        };
    }
  };

  const rightSection = readonly ? undefined : variant === 'selected' ? (
    <ActionIcon
      size="xs"
      radius="xl"
      variant="transparent"
      color="white"
      onClick={(e) => {
        e.stopPropagation();
        onToggle(item);
      }}
    >
      <IconX size={14} />
    </ActionIcon>
  ) : selected ? (
    <ActionIcon
      size="xs"
      radius="xl"
      variant="transparent"
      color="white"
      onClick={(e) => {
        e.stopPropagation();
        onToggle(item);
      }}
    >
      <IconCircleMinus size={14} />
    </ActionIcon>
  ) : (
    <IconCirclePlus size={14} />
  );

  const variantProps = getVariantProps();

  return (
    <Badge
      key={item.id}
      {...variantProps}
      size="lg"
      radius="xl"
      {...(!readonly && { rightSection })}
      onClick={
        variant === 'selected'
          ? undefined
          : selected
            ? undefined // Don't handle click if already selected (let rightSection handle it)
            : () => onToggle(item) // Only handle click for adding (when not selected)
      }
      style={{
        cursor:
          variant === 'selected' ? 'default' : selected ? 'default' : 'pointer',
        transition: 'all 0.2s ease',
        transform: showAnimation ? 'scale(1.05)' : 'scale(1)',
      }}
      styles={{
        root: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      }}
    >
      <Group wrap="nowrap">
        {item.id && (
          <Text size="xs" opacity={0.7}>
            {item.id}
          </Text>
        )}
        <Text size="sm" fw={500} lineClamp={1}>
          {item.name}
        </Text>
      </Group>
    </Badge>
  );
}

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  count: number;
  defaultOpened?: boolean;
  color: string;
}

function CollapsibleSection({
  title,
  subtitle,
  icon,
  children,
  count,
  defaultOpened = true,
  color,
}: CollapsibleSectionProps) {
  const [opened, { toggle }] = useDisclosure(defaultOpened);

  return (
    <Paper withBorder radius="md" p="md" mb="md">
      <UnstyledButton onClick={toggle} w="100%">
        <Group justify="space-between" wrap="nowrap">
          <Group wrap="nowrap">
            <ThemeIcon size="lg" variant="light" color={color} radius="md">
              {icon}
            </ThemeIcon>
            <Box>
              <Text size="sm" fw={600} c="dark.8">
                {title}
              </Text>
              <Text size="xs" c="dimmed">
                {subtitle}
              </Text>
            </Box>
          </Group>
          <Group wrap="nowrap">
            <Indicator
              label={count}
              size={20}
              color={color}
              disabled={count === 0}
            >
              <Box />
            </Indicator>
            <ActionIcon variant="subtle" color="gray">
              {opened ? (
                <IconChevronDown size={16} />
              ) : (
                <IconChevronRight size={16} />
              )}
            </ActionIcon>
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={opened}>
        <Divider my="md" />
        {children}
      </Collapse>
    </Paper>
  );
}

// Main HPO Selection Component
export function HpoSelection({
  patient,
  encounter,
  selectedHpo,
  setSelectedHpo,
  readonly = false,
}: HpoSelectionProps) {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);

  const [animatingBadges, setAnimatingBadges] = useState<Set<string>>(
    new Set()
  );
  // internal: all HPOs fetched from patient's genetic diseases (unfiltered)
  const [pastHpo, setPastHpo] = useState<HpoItem[]>([]);
  const [geneticHpo, setGeneticHpo] = useState<HpoItem[]>([]);
  const [geneticLoading, setGeneticLoading] = useState(false);

  const {
    hpoData,
    searchQuery,
    searchResults,
    isLoading,
    isSaving,
    handleSearchChange,
    clearSearch,
    saveHpoItem,
    deleteHpoItem,
  } = usePatientHpo();

  // 1) Fetch genetic-disease HPOs (run once when patient changes)
  useEffect(() => {
    if (!patient?.id) {
      setGeneticHpo([]);
      return;
    }
    let cancelled = false;
    async function loadGeneticHpos() {
      setGeneticLoading(true);
      try {
        const geneticDiseases: Condition[] = await medplum.searchResources(
          'Condition',
          {
            subject: `Patient/${patient.id}`,
            category: `${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE}|${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.GENETIC_DISEASE}`,
          }
        );

        const allHposArrays = await Promise.all(
          geneticDiseases.map(async (d) => {
            const omimId = getOmimIdFromCondition(d);
            if (!omimId) {
              return [];
            }
            return fetchDiseaseHPO(omimId);
          })
        );

        const merged = Array.from(
          new Map(allHposArrays.flat().map((h) => [h.id, h])).values()
        );
        if (!cancelled) {
          setGeneticHpo(merged);
        }
      } catch (err) {
        console.error('Error loading genetic disease HPOs', err);
        if (!cancelled) {
          setGeneticHpo([]);
        }
      } finally {
        if (!cancelled) setGeneticLoading(false);
      }
    }
    loadGeneticHpos();
    return () => {
      cancelled = true;
    };
  }, [patient, medplum]);

  // 2) Filter HPOs by encounter and set past HPOs (not from current encounter, deduplicated)
  useEffect(() => {
    if (!hpoData) {
      setPastHpo([]);
      return;
    }

    if (!encounter?.id) {
      // If no encounter, show all as past HPOs (deduplicated)
      const uniqueHpoData = Array.from(
        new Map((hpoData ?? []).map((h) => [h.id, h])).values()
      );
      setPastHpo(uniqueHpoData);
      return;
    }

    // Filter HPOs that are NOT from the current encounter
    const pastHpoItems = hpoData.filter((h) => h.encounterId !== encounter.id);

    // Deduplicate by HPO ID (keep only one instance of each HPO)
    const uniquePastHpo = Array.from(
      new Map(pastHpoItems.map((h) => [h.id, h])).values()
    );

    setPastHpo(uniquePastHpo);
  }, [hpoData, encounter]);

  // 3) Sync selectedHpo with HPOs from current encounter only
  useEffect(() => {
    if (!hpoData || !encounter?.id) {
      setSelectedHpo([]);
      return;
    }

    // Filter HPOs that ARE from the current encounter
    const encounterHpos = hpoData.filter((h) => h.encounterId === encounter.id);

    // Only sync if selectedHpo is different from encounter HPOs
    const encounterHpoIds = new Set(encounterHpos.map((h) => h.id));
    const selectedIds = new Set(selectedHpo.map((h) => h.id));

    const areDifferent =
      encounterHpos.length !== selectedHpo.length ||
      encounterHpos.some((h) => !selectedIds.has(h.id)) ||
      selectedHpo.some((h) => !encounterHpoIds.has(h.id));

    if (areDifferent) {
      setSelectedHpo([...encounterHpos]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hpoData, encounter]);

  const handleToggleHpo = async (item: HpoItem) => {
    const isSelected = selectedHpo.some((hpo) => hpo.id === item.id);

    if (isSelected) {
      // Remove from database
      try {
        await deleteHpoItem(item);
        // Update local state immediately - filter by conditionId to only remove the specific Condition
        setSelectedHpo((prev) =>
          prev.filter((hpo) => hpo.conditionId !== item.conditionId)
        );
      } catch (error) {
        console.error('Error deleting HPO:', error);
        // Optionally show error notification to user
      }
    } else {
      // Add to database with animation
      setAnimatingBadges((prev) => new Set(prev).add(item.id));
      try {
        const savedItem = await saveHpoItem(item, encounter);
        if (savedItem) {
          // Update local state immediately
          setSelectedHpo((prev) => {
            if (prev.some((h) => h.id === savedItem.id)) {
              return prev;
            }
            return [...prev, savedItem];
          });
        }
      } catch (error) {
        console.error('Error saving HPO:', error);
        // Optionally show error notification to user
      }

      // Remove animation after delay
      setTimeout(() => {
        setAnimatingBadges((prev) => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
      }, 300);
    }
  };

  const handleSearchSelect = async (value: string) => {
    const selected = searchResults.find((r) => r.label === value);
    if (selected) {
      if (!selectedHpo.some((hpo) => hpo.id === selected.id)) {
        // Use the search result directly, saveHpoItem can handle HpoSearchResult
        setAnimatingBadges((prev) => new Set(prev).add(selected.id));
        try {
          const savedItem = await saveHpoItem(selected, encounter);
          if (savedItem) {
            // Update local state immediately
            setSelectedHpo((prev) => {
              if (prev.some((h) => h.id === savedItem.id)) {
                return prev;
              }
              return [...prev, savedItem];
            });
          }
        } catch (error) {
          console.error('Error saving HPO from search:', error);
          // Optionally show error notification to user
        }

        // Remove animation after delay
        setTimeout(() => {
          setAnimatingBadges((prev) => {
            const newSet = new Set(prev);
            newSet.delete(selected.id);
            return newSet;
          });
        }, 300);
      }

      setTimeout(() => {
        clearSearch();
      }, 0);
    }
  };

  const clearAllSelected = async () => {
    // Delete all HPOs from database
    try {
      await Promise.all(selectedHpo.map((item) => deleteHpoItem(item)));
      // Update local state immediately
      setSelectedHpo([]);
    } catch (error) {
      console.error('Error clearing all HPOs:', error);
      // Optionally show error notification to user
    }
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      {!readonly && (
        <Text c="dimmed" size="sm">
          {t('examination.content.hpo.description', {
            ns: 'patient-pages',
          })}
        </Text>
      )}

      {/* Selected */}
      <Paper withBorder radius="md" p="md" bg="teal.0">
        <Flex justify="space-between" align="center" mb="md">
          <Group gap="xs">
            <IconCheck size={16} style={{ marginRight: 4 }} />
            <Text size="md" fw={600} c="dark.8">
              {t('examination.content.hpo.selectedHpos', {
                ns: 'patient-pages',
              })}
            </Text>
            <Badge size="sm" color="teal" variant="filled" radius="xl">
              {selectedHpo.length}
            </Badge>
          </Group>
          {selectedHpo.length > 0 && !readonly && (
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={clearAllSelected}
              title="Clear all"
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Flex>

        {selectedHpo.length > 0 ? (
          <Stack gap="xs">
            {selectedHpo.map((item) => (
              <EnhancedHpoBadge
                key={`selected-${item.id}`}
                item={item}
                selected={true}
                onToggle={handleToggleHpo}
                variant="selected"
                readonly={readonly}
              />
            ))}
          </Stack>
        ) : (
          <Center py="md">
            <Stack align="center" gap="xs">
              <ThemeIcon size="xl" variant="light" color="gray" radius="xl">
                <IconCheck size={20} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" ta="center">
                {t('examination.content.hpo.noPhenotypeSelected', {
                  ns: 'patient-pages',
                })}
              </Text>
              {!readonly && (
                <Text size="xs" c="dimmed" ta="center">
                  {t('examination.content.hpo.clickToAdd', {
                    ns: 'patient-pages',
                  })}
                </Text>
              )}
            </Stack>
          </Center>
        )}
      </Paper>

      {!readonly && (
        <>
          {/* Search and Sources */}
          {/* Search Section */}
          <Autocomplete
            leftSection={<IconSearch size={16} />}
            placeholder={t('examination.content.hpo.searchPlaceholder', {
              ns: 'patient-pages',
            })}
            value={searchQuery}
            data={searchResults.map((r) => r.label)}
            onChange={handleSearchChange}
            onOptionSubmit={handleSearchSelect}
            rightSection={isLoading || isSaving ? <Loader size="xs" /> : null}
            size="md"
            radius="md"
          />

          {/* Past HPO Section */}
          {pastHpo && pastHpo.length > 0 && (
            <CollapsibleSection
              title={t('examination.content.hpo.patientHpo', {
                ns: 'patient-pages',
              })}
              subtitle={`${t('examination.content.hpo.fromPrevious', {
                ns: 'patient-pages',
              })} - ${pastHpo.length} ${t(
                'examination.content.hpo.phenotypesRecorded',
                {
                  ns: 'patient-pages',
                }
              )}`}
              icon={<IconClipboard size={18} />}
              count={pastHpo.length}
              color="blue"
            >
              <Group gap="xs">
                {pastHpo.map((item) => (
                  <EnhancedHpoBadge
                    key={item.id}
                    item={item}
                    selected={selectedHpo.some((hpo) => hpo.id === item.id)}
                    onToggle={handleToggleHpo}
                    variant="past"
                    showAnimation={animatingBadges.has(item.id)}
                  />
                ))}
              </Group>
            </CollapsibleSection>
          )}

          {/* Genetic Disease HPO Section */}
          {geneticLoading ? (
            <Paper withBorder radius="md" p="md">
              <Center h={60}>
                <Loader />
              </Center>
            </Paper>
          ) : (
            geneticHpo &&
            geneticHpo.length > 0 && (
              <CollapsibleSection
                title={t('examination.content.hpo.geneticDiseaseHpo', {
                  ns: 'patient-pages',
                })}
                subtitle={`${t('examination.content.hpo.associatedCondition', {
                  ns: 'patient-pages',
                })} - ${geneticHpo.length} ${t(
                  'examination.content.hpo.items',
                  {
                    ns: 'patient-pages',
                  }
                )}`}
                icon={<IconDna size={18} />}
                count={geneticHpo.length}
                color="orange"
              >
                <Group gap="xs">
                  {geneticHpo.map((item) => (
                    <EnhancedHpoBadge
                      key={item.id}
                      item={item}
                      selected={selectedHpo.some((hpo) => hpo.id === item.id)}
                      onToggle={handleToggleHpo}
                      variant="genetic"
                      showAnimation={animatingBadges.has(item.id)}
                    />
                  ))}
                </Group>
              </CollapsibleSection>
            )
          )}
        </>
      )}
    </Stack>
  );
}
